import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge, Spinner, Table, Td, Th } from '@/components/admin/ui';
import {
  EmptyState,
  Pagination,
} from '@/components';
import type {
  DictionaryListResponse,
  DictionaryListStatusFilter,
} from '@/lib/admin-types';
import { apiJson } from '@/lib/api';
import { reviewStatusLabel } from './-dictionary-shared';

const PAGE_SIZE = 50;

const STATUS_FILTERS: Array<{ value: DictionaryListStatusFilter; label: string }> =
  [
    { value: 'all', label: 'All' },
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'bookmark', label: 'Bookmark' },
    { value: 'skip', label: 'Skip' },
  ];

type ListSearch = {
  status: DictionaryListStatusFilter;
  page: number;
};

export const Route = createFileRoute('/admin/dictionary/list')({
  validateSearch: (search: Record<string, unknown>): ListSearch => ({
    status:
      STATUS_FILTERS.some((f) => f.value === search.status)
        ? (search.status as DictionaryListStatusFilter)
        : 'all',
    page: Number(search.page) >= 1 ? Number(search.page) : 1,
  }),
  component: DictionaryListPage,
});

function DictionaryListPage() {
  const { status, page } = Route.useSearch();
  const navigate = useNavigate({ from: '/admin/dictionary/list' });
  const [statusDraft, setStatusDraft] = useState(status);

  const listQ = useQuery({
    queryKey: ['admin', 'dictionary', 'list', status, page],
    queryFn: () =>
      apiJson<DictionaryListResponse>(
        `/v1/admin/dictionary/list?review_status=${encodeURIComponent(status)}&page=${page}&page_size=${PAGE_SIZE}`
      ),
  });

  const applyFilter = () => {
    void navigate({
      search: { status: statusDraft, page: 1 },
    });
  };

  if (listQ.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }

  if (listQ.isError) {
    return (
      <EmptyState
        title="Could not load dictionary list"
        hint={(listQ.error as Error)?.message}
      />
    );
  }

  const data = listQ.data;
  if (!data) {
    return <EmptyState title="No data" />;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-sm text-nova-muted-foreground">
          Review status
          <select
            className="mt-1 block rounded-lg border border-nova-border bg-nova-card h-8 px-2.5 text-sm"
            value={statusDraft}
            onChange={(e) =>
              setStatusDraft(e.target.value as DictionaryListStatusFilter)
            }
          >
            {STATUS_FILTERS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="rounded-lg bg-nova-primary/10 px-4 py-2 text-sm font-semibold text-nova-primary"
          onClick={applyFilter}
        >
          Apply filter
        </button>
        <span className="text-sm text-nova-muted-foreground">{data.total} entries</span>
      </div>

      {data.items.length === 0 ? (
        <EmptyState title="No entries match this filter" />
      ) : (
        <>
          <Table>
            <thead>
              <tr>
                <Th>#</Th>
                <Th>Hmong</Th>
                <Th>English</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((item) => (
                <tr key={item.id} className="hover:bg-nova-accent/80">
                  <Td>{item.index + 1}</Td>
                  <Td>
                    <Link
                      to="/admin/dictionary/entry/$id"
                      params={{ id: String(item.id) }}
                      className="font-medium text-nova-primary hover:underline"
                    >
                      {item.headword}
                      {item.homonym_index != null ? (
                        <span className="ml-1 text-nova-muted-foreground">
                          ({item.homonym_index})
                        </span>
                      ) : null}
                    </Link>
                  </Td>
                  <Td className="max-w-md truncate">{item.english || '—'}</Td>
                  <Td>
                    <Badge>{reviewStatusLabel(item.review_status)}</Badge>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>

          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            total={data.total}
            onChange={(nextPage) => {
              void navigate({ search: { status, page: nextPage } });
            }}
          />
        </>
      )}
    </div>
  );
}
